import { useMemo } from 'react';
import { ToothView } from '../../../Enums/ToothView.enum';
import { Tooth, ToothJaw, ToothType } from '../../../../../graphql/graphql';
import { TOOTH_IMAGE_BASE } from '../../../Helpers/periodontalLines/toothImageBase';

interface Props {
	tooth: Tooth;
	view: ToothView;
	renderSize: { w: number; h: number };
}

const BASE_START = 180;
const NUMBER_OF_LINES = 15;
const DEFAULT_SPACING = 20;

const calculateMiddlePointY = (
	toothType: ToothType,
	isBuccal: boolean,
	off: number,
	distance: number,
): number => {
	if (toothType === ToothType.Molar) {
		return off + distance;
	}
	// For non-molar teeth: Buccal uses 3rd line, Lingual/Palatal uses 2nd line
	return isBuccal ? off + 2 * distance : off + distance;
};

export default function useGridCalculation({ tooth, view, renderSize }: Props) {
	const isUpper = tooth.jaw === ToothJaw.Upper;
	const isBuccal = view === ToothView.BUCCAL;
	const isUp = (isUpper && isBuccal) || (!isUpper && !isBuccal);

	const { defaultY, middlePointY, distance } = useMemo(() => {
		const visibleHeight = renderSize.h;
		const zoom = visibleHeight / TOOTH_IMAGE_BASE.h;
		const startOffset = BASE_START * zoom;

		// distance between lines based on grid
		let distance = DEFAULT_SPACING * zoom;
		const maxNeeded = startOffset + (NUMBER_OF_LINES - 1) * distance;
		if (maxNeeded > visibleHeight) {
			distance = (visibleHeight - startOffset) / (NUMBER_OF_LINES - 1);
		}

		const off = isUp
			? TOOTH_IMAGE_BASE.h - visibleHeight + startOffset
			: startOffset;
		const defaultY = off;
		const middlePointY = calculateMiddlePointY(
			tooth.type!,
			isBuccal,
			off,
			distance,
		);

		return {
			defaultY,
			distance,
			middlePointY,
		};
	}, [isUp, renderSize.h, tooth.type, isBuccal]);

	return {
		defaultY,
		middlePointY,
		distance,
		isUp,
	};
}
